import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { vipAPI, creditsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { FaCreditCard, FaCcVisa, FaCcMastercard, FaCcAmex, FaLock, FaCheck, FaTimes, FaCrown, FaCalendarAlt, FaShieldAlt } from 'react-icons/fa';

Modal.setAppElement('#root');

const PaymentModal = ({ isOpen, onClose, package: pkg, type, onSuccess }) => {
  const { user } = useAuth();
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [processing, setProcessing] = useState(false);
  const [showVIPCancel, setShowVIPCancel] = useState(false);
  const [vipStatus, setVipStatus] = useState(null);
  const [cardDetails, setCardDetails] = useState({
    card_number: '',
    expiry: '',
    cvv: '',
  });
  const [cardType, setCardType] = useState(null);

  // Detect card type
  const detectCardType = (number) => {
    const num = number.replace(/\s+/g, '');
    if (num.startsWith('4')) return 'visa';
    if (num.startsWith('5') || num.startsWith('2')) return 'mastercard';
    if (num.startsWith('3')) return 'amex';
    return null;
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setCardDetails({ ...cardDetails, card_number: formatted });
    setCardType(detectCardType(formatted));
  };

  // Check VIP status when modal opens for VIP payments
  useEffect(() => {
    const checkVIPStatus = async () => {
      if (isOpen && type === 'vip') {
        try {
          const response = await vipAPI.getStatus();
          setVipStatus(response.data);
          if (response.data.isVIP) {
            setShowVIPCancel(true);
          }
        } catch (error) {
          console.error('Failed to check VIP status:', error);
        }
      }
    };

    checkVIPStatus();
  }, [isOpen, type]);

  const handleVIPCancel = async () => {
    try {
      setProcessing(true);
      await vipAPI.cancel();
      toast.success('VIP subscription cancelled successfully');
      setShowVIPCancel(false);
      // Modal will close automatically due to onSuccess callback
      onSuccess();
    } catch (error) {
      toast.error('Failed to cancel VIP subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setProcessing(true);

      const paymentData = {
        package_id: pkg.id,
        payment_method: paymentMethod,
        ...cardDetails,
      };

      if (type === 'vip') {
        await vipAPI.subscribe(paymentData);
      } else {
        await creditsAPI.purchase(paymentData);
      }

      onSuccess();
    } catch (error) {
      toast.error('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const renderCardIcon = () => {
    switch (cardType) {
      case 'visa':
        return <FaCcVisa className="text-blue-600 text-xl" />;
      case 'mastercard':
        return <FaCcMastercard className="text-orange-600 text-xl" />;
      case 'amex':
        return <FaCcAmex className="text-green-600 text-xl" />;
      default:
        return <FaCreditCard className="text-gray-400 text-xl" />;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="payment-modal-overlay"
      overlayClassName="payment-modal-backdrop"
    >
      <div className="payment-modal-container">
        {/* Header */}
        <div className="payment-modal-header">
          <div className="payment-modal-header-content">
            <div className="payment-modal-icon">
              <FaLock className="text-white text-lg" />
            </div>
            <div>
              <h2 className="payment-modal-title">Secure Payment</h2>
              <p className="payment-modal-subtitle">Complete your purchase safely</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="payment-modal-close"
          >
            <FaTimes className="text-gray-400 hover:text-white" />
          </button>
        </div>

        <div className="payment-modal-body">
          {showVIPCancel ? (
            /* VIP Cancellation Section */
            <div className="vip-cancel-professional">
              <div className="vip-cancel-header">
                <div className="vip-cancel-icon">
                  <FaCrown className="text-yellow-500 text-2xl" />
                </div>
                <div>
                  <h3 className="vip-cancel-title">Active VIP Membership Detected</h3>
                  <p className="vip-cancel-subtitle">You already have premium access</p>
                </div>
              </div>

              <div className="vip-status-card">
                <div className="vip-status-info">
                  <div className="vip-status-item">
                    <FaCalendarAlt className="text-gray-400" />
                    <span className="vip-status-label">Expires:</span>
                    <span className="vip-status-value">
                      {vipStatus && new Date(vipStatus.expiration * 1000).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="vip-status-item">
                    <FaShieldAlt className="text-gray-400" />
                    <span className="vip-status-label">Status:</span>
                    <span className="vip-status-active">Active</span>
                  </div>
                </div>
              </div>

              <div className="vip-cancel-message">
                <p className="vip-cancel-text">
                  To purchase a new VIP package, you'll need to cancel your current membership first.
                  This action cannot be undone.
                </p>
              </div>

              <div className="vip-cancel-actions">
                <button
                  type="button"
                  onClick={handleVIPCancel}
                  disabled={processing}
                  className="vip-cancel-btn vip-cancel-primary"
                >
                  {processing ? (
                    <>
                      <div className="btn-spinner"></div>
                      Cancelling Membership...
                    </>
                  ) : (
                    <>
                      <FaTimes className="mr-2" />
                      Cancel Current VIP & Continue
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={onClose}
                  className="vip-cancel-btn vip-cancel-secondary"
                >
                  Keep Current Membership
                </button>
              </div>
            </div>
          ) : (
            /* Payment Form */
            <form onSubmit={handleSubmit} className="payment-form">
              {/* Package Summary */}
              <div className="package-summary-card">
                <div className="package-summary-header">
                  <div className="package-summary-icon">
                    {type === 'vip' ? (
                      <FaCrown className="text-yellow-500" />
                    ) : (
                      <FaCreditCard className="text-blue-500" />
                    )}
                  </div>
                  <div className="package-summary-info">
                    <h4 className="package-summary-title">
                      {type === 'vip' ? `${pkg.duration} Days VIP Membership` : `${pkg.credits} Credits Package`}
                    </h4>
                    <p className="package-summary-price">${pkg.price}</p>
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="payment-methods-section">
                <h4 className="section-title">Choose Payment Method</h4>
                <div className="payment-methods-grid">
                  <label className={`payment-method-card ${paymentMethod === 'stripe' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      value="stripe"
                      checked={paymentMethod === 'stripe'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="payment-method-radio"
                    />
                    <div className="payment-method-content">
                      <FaCreditCard className="payment-method-icon" />
                      <div className="payment-method-info">
                        <span className="payment-method-name">Credit Card</span>
                        <span className="payment-method-desc">Visa, Mastercard, Amex</span>
                      </div>
                      {paymentMethod === 'stripe' && <FaCheck className="payment-method-check" />}
                    </div>
                  </label>

                  <label className={`payment-method-card ${paymentMethod === 'paypal' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      value="paypal"
                      checked={paymentMethod === 'paypal'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="payment-method-radio"
                    />
                    <div className="payment-method-content">
                      <img
                        src="/assets/images/paypal.png"
                        alt="PayPal"
                        className="payment-method-paypal-icon"
                      />
                      <div className="payment-method-info">
                        <span className="payment-method-name">PayPal</span>
                        <span className="payment-method-desc">Fast & Secure</span>
                      </div>
                      {paymentMethod === 'paypal' && <FaCheck className="payment-method-check" />}
                    </div>
                  </label>
                </div>
              </div>

              {/* Card Details */}
              {paymentMethod === 'stripe' && (
                <div className="card-details-section">
                  <h4 className="section-title">Card Information</h4>
                  <div className="card-details-form">
                    <div className="form-group">
                      <label className="form-label">Card Number</label>
                      <div className="card-number-input-container">
                        <input
                          type="text"
                          className="card-input card-number-input"
                          placeholder="1234 5678 9012 3456"
                          value={cardDetails.card_number}
                          onChange={handleCardNumberChange}
                          maxLength="19"
                          required
                        />
                        {renderCardIcon()}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Expiry Date</label>
                        <input
                          type="text"
                          className="card-input"
                          placeholder="MM/YY"
                          value={cardDetails.expiry}
                          onChange={(e) => setCardDetails({ ...cardDetails, expiry: e.target.value })}
                          maxLength="5"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">CVV</label>
                        <input
                          type="text"
                          className="card-input"
                          placeholder="123"
                          value={cardDetails.cvv}
                          onChange={(e) => setCardDetails({ ...cardDetails, cvv: e.target.value })}
                          maxLength="4"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Notice */}
              <div className="security-notice">
                <FaShieldAlt className="security-icon" />
                <span className="security-text">
                  Your payment information is encrypted and secure
                </span>
              </div>

              {/* Action Buttons */}
              <div className="payment-actions">
                <button
                  type="button"
                  onClick={onClose}
                  className="payment-btn payment-btn-cancel"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={processing}
                  className="payment-btn payment-btn-pay"
                >
                  {processing ? (
                    <>
                      <div className="btn-spinner"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaLock className="mr-2" />
                      Pay ${pkg.price}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PaymentModal;

